import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { ProductCard } from "@/components/ProductCard";
import { Layout } from "@/components/Layout";
import { CONDITIONS } from "@/lib/format";

export default function Browse() {
  const { lang } = useApp();
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const PAGE_SIZE = 24;
  const category = params.get("category") || "";
  const condition = params.get("condition") || "";
  const q = params.get("q") || "";
  const sort = params.get("sort") || "newest";

  useEffect(() => { api.get("/categories").then(({ data }) => setCats(data)).catch(() => {}); }, []);

  const buildQs = useCallback((skip) => {
    const qs = new URLSearchParams();
    if (category) qs.set("category", category);
    if (condition) qs.set("condition", condition);
    if (q) qs.set("q", q);
    qs.set("sort", sort);
    qs.set("limit", String(PAGE_SIZE));
    qs.set("skip", String(skip));
    return qs.toString();
  }, [category, condition, q, sort]);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/products?${buildQs(0)}`).then(({ data }) => {
      setProducts(data.items || []); setTotal(data.total || 0);
    }).finally(() => setLoading(false));
  }, [buildQs]);

  useEffect(() => { load(); }, [load]);

  const loadMore = () => {
    setLoadingMore(true);
    api.get(`/products?${buildQs(products.length)}`).then(({ data }) => {
      setProducts((prev) => [...prev, ...(data.items || [])]);
    }).finally(() => setLoadingMore(false));
  };

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl font-semibold text-rl-ink">Belanja Preloved</h1>
            <p className="text-sm text-rl-muted mt-1">{q ? `Hasil untuk "${q}" · ` : ""}{total} barang{products.length < total ? ` · ditampilkan ${products.length}` : ""}</p>
          </div>
          <select data-testid="sort-select" value={sort} onChange={(e) => setParam("sort", e.target.value)}
            className="rounded-full border border-rl-border bg-white px-4 py-2 text-sm font-semibold outline-none focus:border-rl-primary">
            <option value="newest">Terbaru</option>
            <option value="price_asc">Harga Terendah</option>
            <option value="price_desc">Harga Tertinggi</option>
          </select>
        </div>

        <div className="mt-6 grid lg:grid-cols-[240px_1fr] gap-8">
          <aside className="space-y-6">
            <div>
              <h3 className="flex items-center gap-2 text-sm font-bold text-rl-ink mb-3"><SlidersHorizontal className="w-4 h-4" />Kategori</h3>
              <div className="space-y-1">
                <button onClick={() => setParam("category", "")} data-testid="filter-cat-all"
                  className={`block w-full text-left text-sm px-3 py-1.5 rounded-lg ${!category ? "bg-rl-primary/10 text-rl-primary font-semibold" : "text-rl-ink2 hover:bg-rl-bg2"}`}>Semua</button>
                {cats.map((c) => (
                  <button key={c.id} onClick={() => setParam("category", c.name_en)} data-testid={`filter-cat-${c.slug}`}
                    className={`block w-full text-left text-sm px-3 py-1.5 rounded-lg ${category === c.name_en ? "bg-rl-primary/10 text-rl-primary font-semibold" : "text-rl-ink2 hover:bg-rl-bg2"}`}>
                    {lang === "id" ? c.name : c.name_en}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-bold text-rl-ink mb-3">Kondisi</h3>
              <div className="space-y-1">
                <button onClick={() => setParam("condition", "")} className={`block w-full text-left text-sm px-3 py-1.5 rounded-lg ${!condition ? "bg-rl-primary/10 text-rl-primary font-semibold" : "text-rl-ink2 hover:bg-rl-bg2"}`}>Semua</button>
                {Object.entries(CONDITIONS).map(([k, v]) => (
                  <button key={k} onClick={() => setParam("condition", k)} data-testid={`filter-cond-${k}`}
                    className={`block w-full text-left text-sm px-3 py-1.5 rounded-lg ${condition === k ? "bg-rl-primary/10 text-rl-primary font-semibold" : "text-rl-ink2 hover:bg-rl-bg2"}`}>{v[lang]}</button>
                ))}
              </div>
            </div>
          </aside>

          <div>
            {loading ? (
              <div className="py-24 text-center text-rl-muted">Memuat produk...</div>
            ) : products.length === 0 ? (
              <div className="py-24 text-center text-rl-muted">Tidak ada produk ditemukan.</div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6">
                  {products.map((p) => <ProductCard key={p.id} p={p} />)}
                </div>
                {products.length < total && (
                  <div className="mt-8 text-center">
                    <button data-testid="load-more" onClick={loadMore} disabled={loadingMore}
                      className="rounded-full border border-rl-border hover:border-rl-primary bg-white text-rl-ink font-semibold px-8 py-3 transition-colors disabled:opacity-50">
                      {loadingMore ? "Memuat..." : `Muat Lebih Banyak (${total - products.length} lagi)`}</button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
